import { diffChars, diffWordsWithSpace } from "diff";
import { Controller } from "stimulus";

const MODES = { words: diffWordsWithSpace, chars: diffChars, result: null };
const STORAGE_KEY = "text-diff-mode";
const MODE_EVENT = "text-diff:mode";

// Renders a GitHub-style inline diff between two texts: removed parts struck
// through in red, added parts highlighted in green. The mode (per word, per
// character, or the plain revised text) is remembered per browser and shared
// by every diff on the page. The server renders the plain revised text inside
// the output target as a fallback.
export default class extends Controller {
  static targets = ["output", "modeButton"];
  static values = { original: String, revised: String };

  connect() {
    this.mode = this.storedMode();
    this.render();
  }

  // Called by a mode button (data-mode="words|chars|result").
  select(event) {
    const mode = event.currentTarget.dataset.mode;
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      // Storage unavailable: the mode just won't persist.
    }
    window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: { mode } }));
  }

  // Keeps every diff on the page in the same mode.
  sync(event) {
    this.mode = event.detail.mode;
    this.render();
  }

  render() {
    this.modeButtonTargets.forEach((button) => {
      button.classList.toggle("active", button.dataset.mode === this.mode);
    });

    const diff = MODES[this.mode];
    const output = this.outputTarget;
    output.textContent = "";
    if (!diff) {
      output.textContent = this.revisedValue;
      return;
    }

    const fragment = document.createDocumentFragment();
    diff(this.originalValue, this.revisedValue).forEach((part) => {
      let node;
      if (part.added) {
        node = document.createElement("ins");
        node.className = "text-diff-added";
      } else if (part.removed) {
        node = document.createElement("del");
        node.className = "text-diff-removed";
      } else {
        node = document.createElement("span");
      }
      node.textContent = part.value;
      fragment.appendChild(node);
    });
    output.appendChild(fragment);
  }

  storedMode() {
    try {
      const mode = window.localStorage.getItem(STORAGE_KEY);
      if (mode in MODES) return mode;
    } catch (e) {
      // Storage unavailable: fall back to the default mode.
    }
    return "words";
  }
}
